// Model Desk (T-342, owner request 2026-11-02): what is sourced to the local Ollama models and how it is going.
// Data: GET /api/models/overview = a snapshot written every minute by the HOST job scripts/ops/delegation/model_desk_snapshot.py (the engine cannot reach Ollama or the model logs).
// Review verdicts: POST /api/models/verdict queues a row; the host job applies it through acceptance_log.record, so the inbox updates on the next snapshot (up to about a minute).
// Rules shown here: models only draft text (L1), never run in the engine or a cron (H-03); a draft is not evidence (H-32). Paper only. Read-only except the verdict queue.
import React, { useCallback, useEffect, useState } from 'react'
import { modelDesk as api } from '../lib/api'
import { Panel, MONO } from '../components/deskParts'

const btn = { ...MONO, fontSize: '0.64rem', letterSpacing: '0.06em', padding: '3px 8px', borderRadius: 4, border: '1px solid #334155', color: '#cbd5e1', background: 'transparent', cursor: 'pointer' }
const th = { textAlign: 'left', color: '#64748b', fontWeight: 400, padding: '2px 8px 2px 0' }
const td = { padding: '2px 8px 2px 0', color: '#cbd5e1' }
const STATE_TONE = { PASS: '#34d399', PASS_SCREEN: '#86efac', CONTINUE: '#fbbf24', TOO_FEW: '#94a3b8', REVIEW: '#f97316', KILL: '#f87171' }
const VERDICTS = [['accepted', 'ACCEPT'], ['light_edit', 'LIGHT EDIT'], ['heavy_edit', 'HEAVY EDIT'], ['rejected', 'REJECT']]
const FLAGS = ['wrong_number', 'missed_red_flag', 'invented_fact']

const ago = (iso) => {
  if (!iso) return 'never'
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000)
  if (s < 90) return `${Math.round(s)} s ago`
  if (s < 5400) return `${Math.round(s / 60)} min ago`
  if (s < 129600) return `${Math.round(s / 3600)} h ago`
  return `${Math.round(s / 86400)} d ago`
}
const pct = (v) => (v == null ? '—' : `${Math.round(v * 100)}%`)

function Chip({ text, color }) {
  return <span style={{ ...MONO, fontSize: '0.62rem', color, border: `1px solid ${color}`, borderRadius: 3, padding: '1px 6px', letterSpacing: '0.06em' }}>{text}</span>
}

function Status({ d }) {
  const stale = d.age_s > 180
  return (
    <Panel title="Status" right={<span style={{ color: stale ? '#f87171' : '#64748b' }}>snapshot {Math.round(d.age_s)} s old{stale ? ' — STALE, is saferfunds-modeldesk.timer running?' : ''}</span>}>
      <div className="flex flex-wrap items-center gap-3" style={{ ...MONO, fontSize: '0.75rem' }}>
        <span>Ollama: {d.ollama_up ? <Chip text="UP" color="#34d399" /> : <Chip text="DOWN" color="#f87171" />}</span>
        <span>Machine: {d.busy ? <Chip text="BUSY" color="#fbbf24" /> : <Chip text="FREE" color="#34d399" />}</span>
        <span>Loaded: {d.loaded.length ? d.loaded.map(m => `${m.model} (${m.size_gb} GB)`).join(', ') : 'none (unloads after 5 min idle)'}</span>
        <span>Last call: {ago(d.last_call_ts)}</span>
        <span>Calls, last {d.calls.window_days} d: {d.calls.total_calls}</span>
        <span>To review: {d.pending.length}</span>
      </div>
      {d.busy && <p style={{ ...MONO, fontSize: '0.68rem', color: '#fbbf24', marginTop: 4 }}>Why busy: {d.busy_reasons.join('; ')}</p>}
    </Panel>
  )
}

function WhoDidWhat({ rows: all }) {
  const [showTests, setShowTests] = useState(false)
  const rows = all.filter(r => showTests || !r.harness)
  return (
    <Panel title="Who did what (S script, M model, C Claude, O owner: model jobs below are the M rows)" right={<label><input type="checkbox" checked={showTests} onChange={e => setShowTests(e.target.checked)} /> show test-harness runs ({all.filter(r => r.harness).length})</label>}>
      {rows.length === 0 ? <p style={{ color: '#64748b' }}>No model calls in the window.</p> : (
        <table style={{ ...MONO, fontSize: '0.68rem', width: '100%' }}>
          <thead><tr>{['Task (purpose)', 'Model', 'Calls', 'Avg s', 'Tok/s', 'Template used', 'Number audit failed', 'Busy override', 'Ticket', 'Last'].map(h => <th key={h} style={th}>{h}</th>)}</tr></thead>
          <tbody>{rows.map((r, i) => (
            <tr key={i}>
              <td style={td}>{r.purpose}</td><td style={td}>{r.model}</td><td style={td}>{r.calls}</td><td style={td}>{r.avg_seconds}</td>
              <td style={td}>{r.avg_tokens_per_s ?? '—'}</td><td style={td}>{r.template_used}</td>
              <td style={{ ...td, color: r.audit_fail ? '#f97316' : '#cbd5e1' }}>{r.audit_fail}</td><td style={td}>{r.busy_override}</td>
              <td style={td}>{r.ticket || '—'}</td><td style={td}>{ago(r.last_ts)}</td>
            </tr>))}</tbody>
        </table>
      )}
      <p style={{ ...MONO, fontSize: '0.62rem', color: '#64748b', marginTop: 6 }}>Tok/s shows a dash for calls made before 2026-11-03 (the audit row did not record speed then). Local models run at roughly 1 to 6 tokens/s here: they save Claude tokens and your attention, not clock time.</p>
    </Panel>
  )
}

function PerDay({ rows }) {
  const max = Math.max(1, ...rows.map(r => r.calls))
  return (
    <Panel title="Calls per day">
      {rows.length === 0 ? <p style={{ color: '#64748b' }}>None in the window.</p> : rows.map(r => (
        <div key={r.day} className="flex items-center gap-2" style={{ ...MONO, fontSize: '0.68rem' }}>
          <span style={{ width: 80, color: '#94a3b8' }}>{r.day}</span>
          <div style={{ width: `${(r.calls / max) * 60}%`, minWidth: 2, height: 8, background: '#38bdf8', borderRadius: 2 }} />
          <span>{r.calls} · avg {r.avg_seconds} s</span>
        </div>))}
    </Panel>
  )
}

function Quality({ rows }) {
  return (
    <Panel title="Quality: accept rate with 95% Wilson interval (rule H-29: 30-day shadow gate)">
      {rows.length === 0 ? <p style={{ color: '#64748b' }}>No reviewed drafts yet. Review the inbox below to start the record.</p> : (
        <table style={{ ...MONO, fontSize: '0.68rem', width: '100%' }}>
          <thead><tr>{['Purpose', 'Reviewed', 'Good', 'Rate', '95% interval', 'Wrong number', 'Template fallbacks', 'State', 'Why'].map(h => <th key={h} style={th}>{h}</th>)}</tr></thead>
          <tbody>{rows.map(r => (
            <tr key={r.purpose}>
              <td style={td}>{r.purpose}</td><td style={td}>{r.reviewed}</td><td style={td}>{r.good}</td>
              <td style={td}>{r.reviewed ? pct(r.good / r.reviewed) : '—'}</td><td style={td}>{pct(r.ci_low)} to {pct(r.ci_high)}</td>
              <td style={{ ...td, color: r.wrong_number ? '#f87171' : '#cbd5e1' }}>{r.wrong_number}</td><td style={td}>{r.template_fallbacks}</td>
              <td style={td}><Chip text={r.state} color={STATE_TONE[r.state] || '#94a3b8'} /></td><td style={{ ...td, color: '#94a3b8' }}>{r.why}</td>
            </tr>))}</tbody>
        </table>
      )}
    </Panel>
  )
}

function Inbox({ items, onDone }) {
  const [open, setOpen] = useState(null)
  const [flags, setFlags] = useState({})
  const [note, setNote] = useState('')
  const [msg, setMsg] = useState(null)
  const [busy, setBusy] = useState(false)
  const send = async (it, verdict) => {
    setBusy(true); setMsg(null)
    try {
      const r = await api.verdict({ staged: it.staged, verdict, flags: FLAGS.filter(f => flags[it.staged]?.[f]), note })
      setMsg(r.success ? 'Queued. The host job applies it within about a minute; the draft leaves the inbox on the next snapshot.' : (r.error || 'failed'))
      if (r.success) { setNote(''); setOpen(null); onDone() }
    } catch (e) { setMsg(String(e.message || e)) }
    setBusy(false)
  }
  return (
    <Panel title={`Review inbox (${items.length}): a draft is not evidence; check every number before accepting`}>
      {msg && <p style={{ ...MONO, fontSize: '0.7rem', color: '#7dd3fc' }}>{msg}</p>}
      {items.length === 0 ? <p style={{ color: '#64748b' }}>Nothing waiting for a verdict.</p> : items.map(it => (
        <div key={it.staged} style={{ borderBottom: '1px solid #1e293b', padding: '4px 0' }}>
          <div className="flex items-center gap-2" style={{ ...MONO, fontSize: '0.7rem' }}>
            <button style={btn} onClick={() => setOpen(open === it.staged ? null : it.staged)}>{open === it.staged ? 'HIDE' : 'OPEN'}</button>
            <span>{it.purpose}</span><span style={{ color: '#94a3b8' }}>{it.model}</span><span style={{ color: '#64748b' }}>{ago(it.ts)}</span>
            {it.used === 'template' && <Chip text="TEMPLATE RELEASED" color="#f97316" />}
            {it.number_audit === 'fail' && <Chip text="NUMBER AUDIT FAILED" color="#f87171" />}
          </div>
          {open === it.staged && (
            <div style={{ marginTop: 4 }}>
              {it.unverified.length > 0 && <p style={{ ...MONO, fontSize: '0.68rem', color: '#f87171' }}>Numbers not in the facts: {it.unverified.join(', ')}</p>}
              <pre style={{ ...MONO, fontSize: '0.68rem', whiteSpace: 'pre-wrap', background: '#0b1220', padding: 8, borderRadius: 4, maxHeight: 280, overflow: 'auto' }}>{it.text || '(draft file not readable)'}</pre>
              <div className="flex flex-wrap items-center gap-3" style={{ ...MONO, fontSize: '0.68rem', margin: '6px 0' }}>
                {FLAGS.map(f => (
                  <label key={f}><input type="checkbox" checked={!!flags[it.staged]?.[f]} onChange={e => setFlags({ ...flags, [it.staged]: { ...(flags[it.staged] || {}), [f]: e.target.checked } })} /> {f}</label>
                ))}
                <input value={note} onChange={e => setNote(e.target.value)} maxLength={300} placeholder="note (optional)" style={{ ...MONO, background: '#0b1220', border: '1px solid #334155', color: '#e2e8f0', padding: '2px 6px', width: 240 }} />
              </div>
              <div className="flex gap-2">{VERDICTS.map(([v, label]) => <button key={v} disabled={busy} style={btn} onClick={() => send(it, v)}>{label}</button>)}</div>
            </div>
          )}
        </div>))}
    </Panel>
  )
}

function Models({ installed }) {
  return (
    <Panel title="Installed models and pins">
      <table style={{ ...MONO, fontSize: '0.68rem', width: '100%' }}>
        <thead><tr>{['Model', 'Digest', 'Pinned', 'Size'].map(h => <th key={h} style={th}>{h}</th>)}</tr></thead>
        <tbody>{installed.map(m => (
          <tr key={m.model}><td style={td}>{m.model}</td><td style={td}>{m.digest12}</td>
            <td style={td}>{m.pinned ? <Chip text="PINNED" color="#34d399" /> : <span style={{ color: '#64748b' }}>not pinned</span>}</td><td style={td}>{m.size_gb} GB</td></tr>))}</tbody>
      </table>
    </Panel>
  )
}

function Rules({ rules }) {
  const [q, setQ] = useState('')
  const shown = rules.filter(r => !q || `${r.id} ${r.title}`.toLowerCase().includes(q.toLowerCase()))
  return (
    <Panel title="Hard rules lookup (why a model was refused or fell back to a template)">
      <input value={q} onChange={e => setQ(e.target.value)} placeholder="search, e.g. H-10 or number" style={{ ...MONO, background: '#0b1220', border: '1px solid #334155', color: '#e2e8f0', padding: '2px 6px', width: 260, marginBottom: 6 }} />
      {shown.map(r => <p key={r.id} style={{ ...MONO, fontSize: '0.68rem' }}><b style={{ color: '#7dd3fc' }}>{r.id}</b> {r.title}</p>)}
    </Panel>
  )
}

export default function ModelDesk() {
  const [d, setD] = useState(null)
  const [err, setErr] = useState(null)
  const load = useCallback(() => { api.overview().then(r => { setD(r); setErr(null) }).catch(e => setErr(String(e.message || e))) }, [])
  useEffect(() => { load(); const t = setInterval(load, 20000); return () => clearInterval(t) }, [load])
  return (
    <div className="p-3 space-y-3" style={{ color: '#cbd5e1' }}>
      <div style={{ ...MONO, fontSize: '0.8rem', color: '#e2e8f0' }}>MODEL DESK <span style={{ color: '#64748b', fontSize: '0.65rem' }}>local Ollama models: draft text only, never in the engine, never evidence. Page refreshes every 20 s from a once-a-minute host snapshot.</span></div>
      {err && <p style={{ color: '#fca5a5' }}>{err}</p>}
      {d && d.available === false && <p style={{ color: '#fbbf24' }}>No snapshot yet: {d.reason}. Install the timer: sudo bash scripts/ops/linux/install_modeldesk_timer.sh</p>}
      {d && d.available && (<>
        <Status d={d} />
        <WhoDidWhat rows={d.calls.who_did_what} />
        <div className="grid gap-3" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(420px, 1fr))' }}>
          <PerDay rows={d.calls.per_day} />
          <Models installed={d.installed} />
        </div>
        <Quality rows={d.accept} />
        <Inbox items={d.pending} onDone={() => setTimeout(load, 1500)} />
        <Rules rules={d.hard_rules} />
      </>)}
    </div>
  )
}
